import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  Panel,
  Label,
  RadioButton,
  FormControl,
  Notification,
} from '@uhg-harmony/uitk-react';
import {
  Arrow,
  StyledPanelTitle,
} from 'builder/scenes/SiteBuilder/SettingsPanel/SettingsPanel';
import {
  Padding,
  PaddingData,
} from 'builder/components/WidgetConfigurations/Padding';
import { Border } from 'builder/components/WidgetConfigurations/Border';
import { StyledPanel, SizeLocationPanel } from '../Text/TextWidgetControl';
import {
  SizeLocationData,
  WidgetSizeLocation,
} from 'builder/components/WidgetSizeLocation';
import { Config } from './types';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Input } from 'shared/theme/link-ui-react/Input';

const StyledRadio = styled(RadioButton)`
  & > label {
    font-family: OptumSans, sans-serif;
  }
`;

const StyledInput = styled(Input)`
  font-family: OptumSans, sans-serif;
`;

const StyledURL = styled.i`
  font-family: OptumSans, sans-serif;
  padding: 0.25rem;
  font-size: 0.85rem;
`;

const PanelTop = styled.div`
  .jSWwIc {
    padding: 0;
    border: none;
  }
`;

const HtmlWidgetControl: React.FC<WidgetControlProps<Config>> = props => {
  const { value, onChange, widgetState, features, fetchFeatures } = props;
  const { inputUrl: savedUrl } = widgetState.config;
  const [checked, setChecked] = useState('web-address');
  const [urlInput, setUrlInput] = useState(savedUrl || '');
  const [urlPreview, setUrlPreview] = useState(
    savedUrl ? `https://${savedUrl}` : ''
  );
  const [urlError, setUrlError] = useState('');

  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    if (!hasFetched && !features?.whiteListDomains?.length) {
      fetchFeatures('iframe_domain_whitelist.json');
      setHasFetched(true); // prevent infinite loop
    }
  }, [hasFetched, features, fetchFeatures]);

  useEffect(() => {
    setUrlInput(savedUrl || '');
    setUrlPreview(savedUrl ? `https://${savedUrl}` : '');
  }, [savedUrl]);

  const handleOptionChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setChecked(newValue);
    if (newValue === 'web-address') {
      setUrlInput('');
      setUrlPreview('');
      setUrlError('');
      onChange({
        ...value,
        inputUrl: '',
        urlPreview: '',
      });
    }
  };

  const modifyUrlData = (url: string) => {
    switch (url) {
      case 'google.com':
      case 'www.google.com':
        toast('Google URL was updated', {
          type: 'warning',
          theme: 'colored',
        });
        return 'google.com/webhp?igu=1';
      default:
        return url;
    }
  };

  const validateAndUpdate = (rawUrl: string) => {
    setUrlInput(rawUrl);

    if (!rawUrl) {
      setUrlPreview('');
      setUrlError('');
      onChange({
        ...value,
        inputUrl: '',
        urlPreview: '',
      });
      return;
    }

    const modifiedUrl = modifyUrlData(rawUrl);
    setUrlPreview(`https://${modifiedUrl}`);

    if (rawUrl.length < 4 || !rawUrl.includes('.')) {
      setUrlError('');
      return;
    }

    try {
      const parsed = new URL('https://' + modifiedUrl);
      const domain = parsed.hostname.toLowerCase();

      const allowedDomains = features?.whiteListDomains || [];

      const isAllowed = allowedDomains.some(
        allowed => domain === allowed || domain.endsWith('.' + allowed)
      );

      if (isAllowed) {
        setUrlError('');
        onChange({
          ...value,
          inputUrl: rawUrl,
          urlPreview: `https://${modifiedUrl}`,
        });
      } else {
        setUrlError(`Only ${allowedDomains.join(', ')} domains are allowed.`);
      }
    } catch {
      setUrlError('');
    }
  };

  const handleLineColor = (data: any) => {
    onChange({
      ...value,
      lineColor: data,
    });
  };

  const updateWidgetSizeLocation = (sizeLoc: SizeLocationData) => {
    onChange({
      ...value,
      width: sizeLoc.width,
      height: sizeLoc.height,
      xLocation: sizeLoc.xLocation,
      yLocation: sizeLoc.yLocation,
      uniqueId: sizeLoc.uniqueId,
    });
  };

  const updatePaddingData = (data: PaddingData) => {
    onChange({
      ...value,
      paddingData: data,
    });
  };

  const updateBorderData = (data: BorderData) => {
    onChange({
      ...value,
      borderData: data,
    });
  };

  return (
    <Panel.Group>
      <StyledPanel>
        <Panel
          title={<Label style={StyledPanelTitle}>General</Label>}
          icon={Arrow}
          data-test-id="general-panel"
        >
          <PanelTop>
            <FormControl id={'radio-group-controlled'}>
              <StyledRadio
                data-test-id="radio-url"
                name={'radio-group-controlled'}
                value={'web-address'}
                onChange={handleOptionChange}
                checked={checked === 'web-address'}
              >
                Web Address
              </StyledRadio>
              <StyledRadio
                data-test-id="radio-snippet"
                name={'radio-group-controlled'}
                value={'snippet'}
                onChange={handleOptionChange}
                checked={checked === 'snippet'}
                disabled={true}
              >
                Code Snippet
              </StyledRadio>
            </FormControl>

            {checked === 'web-address' && (
              <>
                {urlError && (
                  <Notification
                    className="mv-m"
                    variant="error"
                    id="notification-error"
                  >
                    {urlError}
                  </Notification>
                )}
                <StyledInput
                  data-test-id="url-input"
                  type="url"
                  value={urlInput}
                  onChange={e =>
                    validateAndUpdate(
                      (e.target as HTMLInputElement).value.trim()
                    )
                  }
                />
                <StyledURL>{urlPreview ? urlPreview : 'https://'}</StyledURL>
              </>
            )}
          </PanelTop>
        </Panel>
      </StyledPanel>

      <StyledPanel>
        <SizeLocationPanel
          title={<Label style={StyledPanelTitle}>Size and Location</Label>}
          initiallyClosed
          icon={Arrow}
          data-test-id="html-size-location-panel"
        >
          <WidgetSizeLocation
            data-test-id="html-widget-size-location"
            sizeLocation={{
              xLocation: value.xLocation,
              yLocation: value.yLocation,
              width: value.width,
              height: value.height,
              uniqueId: value.uniqueId,
            }}
            updateWidgetSizeLocation={updateWidgetSizeLocation}
            activeWidgetId={widgetState.id}
          />
        </SizeLocationPanel>
      </StyledPanel>

      <StyledPanel>
        <Panel
          data-test-id="html-border-panel"
          title={<Label style={StyledPanelTitle}>Border</Label>}
          initiallyClosed
          icon={Arrow}
        >
          <Border
            handleLineColor={handleLineColor}
            data-test-id="html-border"
            updateBorderData={updateBorderData}
            widgetState={widgetState}
            pages={props.pages}
            site={props.site}
            updateSite={props.updateSite}
            saveSite={props.saveSite}
          />
        </Panel>
      </StyledPanel>

      <StyledPanel>
        <Panel
          data-test-id="html-padding-panel"
          title={<Label style={StyledPanelTitle}>Padding</Label>}
          initiallyClosed
          icon={Arrow}
        >
          <Padding
            data-test-id="html-padding"
            paddingData={updatePaddingData}
            widgetState={widgetState}
          />
        </Panel>
      </StyledPanel>
    </Panel.Group>
  );
};

export default HtmlWidgetControl;
